import { useEffect, useState } from "react";
import { singletonHook } from "react-singleton-hook";

// Interfaces
import { BillingData, Coach, CoachAccessCode } from "../interfaces/Coach";

// Firebase
import { auth, firestore } from "../firebase/firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { User } from "firebase/auth";

const emptyLegacyCardInformation: BillingData["cardInformation"] = {
    cardNumber: "",
    cardholderName: "",
    expirationDate: "",
    cvc: "",
};

const initState = {
    authUser: undefined,
    coachCode: null,
    billingData: null,
    currentCoach: undefined,
    emailIsVerified: undefined,

    loading: false,
    fetchingCoach: true,
    fetchingBillingData: true,

    setLoading: (value: any) => console.log("setLoading() ---> uninitialized"),
    setEmailIsVerified: (value: any) => console.log("setEmailIsVerified() ---> uninitialized"),

    // This is now a valid function that matches the type () => Promise<void>
    logout: () => Promise.resolve(),
};

const useFetchCoachImpl = () => {
    // States
    const [loading, setLoading] = useState(false);
    const [fetchingCoach, setFetchingCoach] = useState(false);
    const [fetchingBillingData, setFetchingBillingData] = useState(false);

    const [authUser, setAuthUser] = useState<User | null | undefined>(undefined);
    const [currentCoach, setCurrentCoach] = useState<Coach | null | undefined>(undefined);
    const [emailIsVerified, setEmailIsVerified] = useState<boolean | null | undefined>(undefined);

    const [coachCode, setCoachCode] = useState<string | null>(null);
    const [billingData, setBillingData] = useState<BillingData | null>(null);

    // unsubs
    let unsub: (() => void) | null = null;
    let unsubBillingData: (() => void) | null = null;
    let unsubCoachCode: (() => void) | null = null;

    // let unsub: (() => void) | null = null;
    // let unsubBillingData: (() => void) | null = null;
    // let unsubCoachCode: (() => void) | null = null;

    // const byPassSignIn = async (coachId: string) => {
    //     try {
    //         unsub = onSnapshot(doc(firestore, "Coaches", coachId), coachDoc => {
    //             const data = coachDoc.data() as Coach;

    //             setCurrentCoach(data);
    //             setFetchingCoach(false);
    //             setFetchingBillingData(true);

    //             unsubBillingData = onSnapshot(doc(firestore, `Coaches/${coachId}/Private/billingData`), (doc: any) => {
    //                 const billingData = doc.data() as BillingData;
    //                 setBillingData(billingData);
    //                 setFetchingBillingData(false);
    //             });

    //             unsubCoachCode = onSnapshot(doc(firestore, `Coaches/${coachId}/Private/coachAccessCode`), (doc: any) => {
    //                 const coachCode = doc.data() as CoachAccessCode;
    //                 if (!coachCode?.code) return;
    //                 setCoachCode(coachCode.code);
    //             });
    //         });
    //     } catch (e) {
    //         console.error(e);
    //     }
    // };

    // useEffect(() => {
    //     byPassSignIn("zBnzafHd7RVmpI6F2GdxWa1pov73");

    //     return () => {
    //         if (unsub) unsub();
    //         if (unsubBillingData) unsubBillingData();
    //         if (unsubCoachCode) unsubCoachCode();
    //     };
    // }, []);

    useEffect(() => {
        auth.onAuthStateChanged(async res => {
            try {
                setAuthUser(auth.currentUser);
                if (res) {
                    setFetchingCoach(true);

                    setEmailIsVerified(res.emailVerified);

                    unsub = onSnapshot(doc(firestore, "Coaches", res.uid), doc => {
                        if (!doc.exists()) {
                            setCurrentCoach(null);
                            setFetchingCoach(false);
                            return;
                        }

                        const data = doc.data() as Coach;

                        setCurrentCoach(data);
                        setFetchingCoach(false);
                    });

                    // Fetch the billingData from the 'Private' subcollection
                    setFetchingBillingData(true);
                    unsubBillingData = onSnapshot(doc(firestore, `Coaches/${res.uid}/Private/billingData`), doc => {
                        const billingData = {
                            ...(doc.data() as Partial<BillingData>),
                            cardInformation: {
                                ...emptyLegacyCardInformation,
                                ...((doc.data() as Partial<BillingData>)?.cardInformation ?? {}),
                            },
                        } as BillingData;
                        setBillingData(billingData);
                        setFetchingBillingData(false);
                    });

                    unsubCoachCode = onSnapshot(doc(firestore, `Coaches/${res.uid}/Private/coachAccessCode`), doc => {
                        const coachCode = doc.data() as CoachAccessCode;
                        if (!coachCode?.code) return;
                        setCoachCode(coachCode.code);
                    });
                } else {
                    setCurrentCoach(null);
                    setEmailIsVerified(null);

                    setBillingData(null);
                    setFetchingCoach(false);
                    setFetchingBillingData(false);
                }
            } catch (e) {
                console.error("onAuthStateChanged() Error:", e);
            }
        });

        return () => {
            if (unsub) unsub();
            if (unsubBillingData) unsubBillingData();
            if (unsubCoachCode) unsubCoachCode();
        };
    }, []);

    const logout = async () => {
        try {
            setAuthUser(null);
            setCoachCode(null);
            setBillingData(null);
            setCurrentCoach(null);
            setEmailIsVerified(null);

            await auth.signOut();

            if (unsub) unsub();
            if (unsubCoachCode) unsubCoachCode();
            if (unsubBillingData) unsubBillingData();

            // window.location.replace("/login");
        } catch (e) {
            console.error("logout() ---> Error:", e);
        }
    };

    return {
        authUser,
        currentCoach,
        fetchingCoach,
        loading,
        setLoading,
        billingData,
        fetchingBillingData,
        coachCode,
        emailIsVerified,
        setEmailIsVerified,
        logout,
    };
};

export const useFetchCoach = singletonHook(initState, useFetchCoachImpl);
